import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import BasePageLayout from '../../components/BasePageLayout';
import RichContentDisplay from '../../components/content/RichContentDisplay';
import AdminAnnouncements from '../../components/admin/AdminAnnouncements';
import Tooltip from '../../components/common/Tooltip';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { forumService } from '../../services/forumService';
import { supabase } from '../../services/supabaseClient';
import type { ForumCategory, ForumTopic } from '../../types/forum';
import PageLoader from '../../components/common/PageLoader';
import '../../styles/pages/ForumCategory.css';
import '../../styles/pages/TasksHubModern.css';

const ForumCategoryPage: React.FC = () => {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const { user } = useAuth();
  const { t } = useLanguage();

  const [category, setCategory] = useState<ForumCategory | null>(null);
  const [topics, setTopics] = useState<ForumTopic[]>([]);
  const [latestTopic, setLatestTopic] = useState<ForumTopic | null>(null);
  const [topicImages, setTopicImages] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(true);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalTopics, setTotalTopics] = useState(0);
  const [sortBy, setSortBy] = useState<'newest' | 'most_active' | 'oldest' | 'most_popular'>('most_active');
  const [filterTag, setFilterTag] = useState<string>('');
  const [pinningTopics, setPinningTopics] = useState<Set<string>>(new Set());

  const topicsPerPage = 20;
  const isAdmin = user?.role === 'admin';

  // Memoize loadCategory to prevent unnecessary recreations
  const loadCategory = useCallback(async () => {
    try {
      if (!categorySlug) return;

      // Load category from database using the direct method
      const foundCategory = await forumService.getCategoryBySlugDirect(categorySlug);

      if (foundCategory) {
        setCategory(foundCategory);
      }
    } catch (error) {
      console.error('Error loading category:', error);
    } finally {
      setLoading(false);
    }
  }, [categorySlug]);

  useEffect(() => {
    if (categorySlug) {
      setPage(1); // Reset to first page when category changes
      loadCategory();
    }
  }, [categorySlug, loadCategory]);

  useEffect(() => {
    if (categorySlug) {
      loadTopics(); // Load topics for current page
    }
  }, [categorySlug, page, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!category?.id) return;

    console.log('🔄 Setting up real-time subscriptions for category:', category.id);

    // Debounce function to prevent too many rapid updates
    let updateTimeout: NodeJS.Timeout;
    const debouncedUpdate = (updateFunction: () => Promise<void>) => {
      clearTimeout(updateTimeout);
      updateTimeout = setTimeout(updateFunction, 1000); // 1 second debounce
    };

    const subscription = supabase
      .channel(`category:${category.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'forum_categories',
          filter: `id=eq.${category.id}`
        },
        (payload: any) => {
          console.log('🔄 Real-time: Category updated:', payload);
          if (payload.new) {
            setCategory(payload.new as any);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'forum_topics',
          filter: `category_id=eq.${category.id}`
        },
        async (payload: any) => {
          console.log('🔔 Real-time: New topic in category:', payload);

          // Debounced reload of topics to include the new one
          debouncedUpdate(async () => {
            try {
              const result = await forumService.getTopicsByCategorySlugWithSort(categorySlug!, page, topicsPerPage, 'most_active');
              setTopics(result.topics);
              setTotalTopics(result.totalCount);
              console.log('✅ Topics refreshed with new topic');
            } catch (error) {
              console.error('❌ Error refreshing topics:', error);
            }
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'forum_topics',
          filter: `category_id=eq.${category.id}`
        },
        async (payload: any) => {
          console.log('🔄 Real-time: Topic updated in category:', payload);

          // Debounced update of specific topic or reload if needed
          debouncedUpdate(async () => {
            try {
              const result = await forumService.getTopicsByCategorySlugWithSort(categorySlug!, page, topicsPerPage, 'most_active');
              setTopics(result.topics);
              setTotalTopics(result.totalCount);
              console.log('✅ Topics refreshed with updated topic');
            } catch (error) {
              console.error('❌ Error refreshing topics:', error);
            }
          });
        }
      )
      .subscribe((status: any, err: any) => {
        console.log('📡 Category subscription status:', status);
        if (err) {
          console.error('❌ Category subscription error:', err);
        }
      });

    return () => {
      clearTimeout(updateTimeout);
      console.log('🧹 Cleaning up category subscription');
      subscription.unsubscribe();
    };
  }, [category?.id, categorySlug, page, topicsPerPage]);



  const loadTopics = useCallback(async () => {
    try {
      if (!categorySlug) return;
      setTopicsLoading(true);

      // Load topics for current page with selected sort option
      const result = await forumService.getTopicsByCategorySlugWithSort(categorySlug, page, topicsPerPage, sortBy);

      setTopics(result.topics);
      setTotalTopics(result.totalCount);

      // Set the latest topic (first topic when sorted by newest)
      if (result.topics.length > 0) {
        // Get the latest topic by created_at date
        const latest = [...result.topics].sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )[0];
        setLatestTopic(latest);
      }

      // Load preview images for the topics
      if (result.topics.length > 0) {
        const topicIds = result.topics.map(topic => topic.id);
        const images = await forumService.getTopicPreviewImages(topicIds);
        setTopicImages(prev => ({ ...prev, ...images }));
      }

    } catch (error) {
      console.error('Error loading topics:', error);
      setTopics([]);
      setTotalTopics(0);
    } finally {
      setTopicsLoading(false);
    }
  }, [categorySlug, page, topicsPerPage, sortBy]);

  // NOTE: Duplicate realtime subscription removed - using the comprehensive one above (lines 49-138)

  const totalPages = Math.ceil(totalTopics / topicsPerPage);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const startPage = Math.max(1, page - 2);
    const endPage = Math.min(totalPages, page + 2);

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="pagination">
        <button
          className={`page-btn ${page === 1 ? 'disabled' : ''}`}
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 1}
        >
          &laquo; Prev
        </button>

        {pageNumbers.map(pageNum => (
          <button
            key={pageNum}
            className={`page-btn ${pageNum === page ? 'active' : ''}`}
            onClick={() => handlePageChange(pageNum)}
          >
            {pageNum}
          </button>
        ))}

        <button
          className={`page-btn ${page === totalPages ? 'disabled' : ''}`}
          onClick={() => handlePageChange(page + 1)}
          disabled={page === totalPages}
        >
          Next &raquo;
        </button>
      </div>
    );
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return t('forums.time.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('forums.time.hoursAgo', { count: diffHours });
    return t('forums.time.daysAgo', { count: diffDays });
  };

  // Handle pin/unpin topic
  const handleTogglePin = async (topicId: string, currentPinStatus: boolean) => {
    if (!isAdmin || pinningTopics.has(topicId)) return;

    try {
      setPinningTopics(prev => new Set(prev).add(topicId));

      await forumService.pinTopic(topicId, !currentPinStatus);

      // Update the topic in the current state
      setTopics(prevTopics =>
        prevTopics.map(topic =>
          topic.id === topicId
            ? { ...topic, is_pinned: !currentPinStatus }
            : topic
        )
      );
    } catch (error) {
      console.error('Error toggling pin status:', error);
      // You might want to show a user-friendly error message here
    } finally {
      setPinningTopics(prev => {
        const newSet = new Set(prev);
        newSet.delete(topicId);
        return newSet;
      });
    }
  };

  // Add JavaScript for better tooltip behavior
  useEffect(() => {
    const handleTooltipClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.closest('.category-info-hint')) {
        e.preventDefault();
        const button = target.closest('.category-info-hint') as HTMLElement;
        const tooltip = button?.querySelector('.tooltip-content') as HTMLElement;
        if (tooltip && button) {
          // Close other tooltips
          document.querySelectorAll('.tooltip-content.show').forEach(el => {
            if (el !== tooltip) {
              el.classList.remove('show');
              // Reset aria-expanded for other buttons
              const otherButton = el.closest('.category-info-hint') as HTMLElement;
              if (otherButton) {
                otherButton.setAttribute('aria-expanded', 'false');
              }
            }
          });

          const isShown = tooltip.classList.contains('show');

          // Check if there's enough space above for the tooltip
          const rect = button.getBoundingClientRect();
          const spaceAbove = rect.top;
          const spaceLeft = rect.left;
          const spaceRight = window.innerWidth - rect.right;
          const tooltipHeight = 120; // Approximate tooltip height
          const tooltipWidth = 400; // Approximate tooltip width

          if (spaceAbove < tooltipHeight) {
            tooltip.classList.add('below');
          } else {
            tooltip.classList.remove('below');
          }

          // Reset alignment classes
          tooltip.classList.remove('align-left', 'align-right');

          // Adjust horizontal positioning if needed
          if (spaceLeft < tooltipWidth / 2) {
            tooltip.classList.add('align-left');
          } else if (spaceRight < tooltipWidth / 2) {
            tooltip.classList.add('align-right');
          }

          tooltip.classList.toggle('show');
          button.setAttribute('aria-expanded', (!isShown).toString());
        }
      } else {
        // Close all tooltips when clicking outside
        document.querySelectorAll('.tooltip-content.show').forEach(el => {
          el.classList.remove('show');
          const button = el.closest('.category-info-hint') as HTMLElement;
          if (button) {
            button.setAttribute('aria-expanded', 'false');
          }
        });
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Handle Escape key to close tooltips
      if (e.key === 'Escape') {
        document.querySelectorAll('.tooltip-content.show').forEach(el => {
          el.classList.remove('show');
          const button = el.closest('.category-info-hint') as HTMLElement;
          if (button) {
            button.setAttribute('aria-expanded', 'false');
          }
        });
      }

      // Handle Enter/Space on hint button
      if ((e.key === 'Enter' || e.key === ' ') && (e.target as HTMLElement).classList.contains('category-info-hint')) {
        e.preventDefault();
        const button = e.target as HTMLElement;
        const tooltip = button.querySelector('.tooltip-content') as HTMLElement;
        if (tooltip) {
          const isShown = tooltip.classList.contains('show');

          // Check if there's enough space above for the tooltip
          const rect = button.getBoundingClientRect();
          const spaceAbove = rect.top;
          const spaceLeft = rect.left;
          const spaceRight = window.innerWidth - rect.right;
          const tooltipHeight = 120; // Approximate tooltip height
          const tooltipWidth = 400; // Approximate tooltip width

          if (spaceAbove < tooltipHeight) {
            tooltip.classList.add('below');
          } else {
            tooltip.classList.remove('below');
          }

          // Reset alignment classes
          tooltip.classList.remove('align-left', 'align-right');

          // Adjust horizontal positioning if needed
          if (spaceLeft < tooltipWidth / 2) {
            tooltip.classList.add('align-left');
          } else if (spaceRight < tooltipWidth / 2) {
            tooltip.classList.add('align-right');
          }

          tooltip.classList.toggle('show');
          button.setAttribute('aria-expanded', (!isShown).toString());
        }
      }
    };

    document.addEventListener('click', handleTooltipClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('click', handleTooltipClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  if (loading) {
    return (
      <BasePageLayout background="white" padding="none" maxWidth="xl">
        <PageLoader fullPage text={t('forums.categoryPage.loading')} />
      </BasePageLayout>
    );
  }

  if (!category) {
    return (
      <BasePageLayout background="white" padding="none" maxWidth="xl">
        <div className="forum-category-container">
          <div className="text-center" style={{ padding: '3rem' }}>
            <h3>{t('forums.categoryPage.notFound')}</h3>
            <Link to="/forums" className="btn btn-primary">{t('forums.categoryPage.backToForums')}</Link>
          </div>
        </div>
      </BasePageLayout>
    );
  }

  return (
    <BasePageLayout background="white" padding="none" maxWidth="xl">
      <div className="tasks-hub-container forum-category-container">
        {/* Category Header matching ForumsPage structure */}
        <div className="tasks-hub-header">
          <div className="tasks-hub-header-content">
            <div className="category-header-modern">
              <div className="category-breadcrumb">
                <Link to="/forums" className="breadcrumb-link">
                  <i className="bi bi-arrow-left"></i>
                  {t('forums.categoryPage.backToForums')}
                </Link>
              </div>

              <div className="category-header-content">
                <div className="category-icon-wrapper" style={{ backgroundColor: `${category.color}15`, color: category.color }}>
                  <span className="category-icon">{category.icon}</span>
                </div>
                <div className="category-info">
                  <h1>{t(`forums.categories.titles.${category.slug}`, { defaultValue: category.title })}</h1>
                </div>
              </div>


            </div >
          </div >
        </div >

        {/* Main Content matching ForumsPage structure */}
        < div className="tasks-hub-content" >
          <div className="tasks-hub-main forum-category-main-content">
            {/* Admin Announcements - Moved from header to main content */}
            <div className="category-announcements-main">
              <AdminAnnouncements
                categoryId={category.id}
                categorySlug={categorySlug || ''}
              />
            </div>

            {/* Top Controls Section - New Topic and Pagination in same row */}
            <div className="forum-top-controls">
              <div className="forum-controls-row">
                {/* Left side - New Topic Button */}
                <div className="forum-controls-left">
                  {user && (
                    <Link
                      to={`/forums/category/${categorySlug}/create-topic`}
                      className="new-topic-btn-compact"
                    >
                      <i className="bi bi-plus"></i>
                      {t('forums.categoryPage.controls.newTopic')}
                    </Link>
                  )}
                </div>

                {/* Right side - Pagination and Sort */}
                <div className="forum-controls-right">
                  {/* Sort Options */}
                  <div className="sort-controls-compact">
                    <select
                      value={sortBy}
                      onChange={(e) => {
                        setPage(1); // Reset to first page when sort changes
                        setSortBy(e.target.value as any);
                      }}
                      className="sort-select-compact"
                    >
                      <option value="most_active">{t('forums.categoryPage.controls.sort.mostActive')}</option>
                      <option value="newest">{t('forums.categoryPage.controls.sort.newest')}</option>
                      <option value="oldest">{t('forums.categoryPage.controls.sort.oldest')}</option>
                      <option value="most_popular">{t('forums.categoryPage.controls.sort.mostPopular')}</option>
                    </select>
                  </div>

                  {/* Compact Pagination */}
                  {totalPages > 1 && (
                    <div className="pagination-compact">
                      <button
                        className={`page-btn-compact ${page === 1 ? 'disabled' : ''}`}
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                      >
                        <i className="bi bi-chevron-left"></i>
                      </button>

                      <span className="page-info">
                        {page} / {totalPages}
                      </span>

                      <button
                        className={`page-btn-compact ${page === totalPages ? 'disabled' : ''}`}
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === totalPages}
                      >
                        <i className="bi bi-chevron-right"></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Tag Filters */}
              {/* {category.subcategories && category.subcategories.length > 0 && (
                <div className="forum-tag-filters">
                  <button
                    className={`filter-tag ${filterTag === '' ? 'active' : ''}`}
                    onClick={() => setFilterTag('')}
                  >
                    All
                  </button>
                  {category.subcategories.map((subcategory, index) => (
                    <button
                      key={index}
                      className={`filter-tag ${filterTag === subcategory ? 'active' : ''}`}
                      onClick={() => setFilterTag(filterTag === subcategory ? '' : subcategory)}
                    >
                      {subcategory}
                    </button>
                  ))}
                </div>
              )} */}
            </div>

            {/* Topics Table */}
            <div className="topics-table-container">
              {/* Table Header */}
              <div className="topics-table-header">
                <div className="header-cell topic-header">{t('forums.categoryPage.table.topic')}</div>
                <div className="header-cell author-header">{t('forums.categoryPage.table.author')}</div>
                <div className="header-cell stats-header">{t('forums.categoryPage.table.stats')}</div>
                <div className="header-cell last-post-header">{t('forums.categoryPage.table.lastPost')}</div>
              </div>

              {/* Topics List */}
              {topicsLoading && page === 1 ? (
                <div className="loading-topics">
                  <div className="loading-ring"></div>
                  <span>{t('forums.categoryPage.table.loading')}</span>
                </div>
              ) : topics.length === 0 ? (
                <div className="empty-topics-message">
                  <div className="empty-icon">💬</div>
                  <h3>{t('forums.categoryPage.table.emptyTitle')}</h3>
                  <p>{t('forums.categoryPage.table.emptySubtitle')}</p>
                  {user && (
                    <Link
                      to={`/forums/category/${categorySlug}/create-topic`}
                      className="create-topic-cta"
                    >
                      <i className="bi bi-plus-circle-fill"></i>
                      {t('forums.categoryPage.controls.startDiscussion')}
                    </Link>
                  )}
                </div>
              ) : (
                <div className="topics-table-body">
                  {topics.map((topic) => (
                    <div key={topic.id} className="topic-row">
                      {/* Topic Info */}
                      <div className="topic-cell topic-info">
                        <div className="topic-status-icon">
                          {topic.is_pinned ? (
                            <i className="bi bi-pin-angle-fill topic-icon pinned" title={t('forums.categoryPage.table.pinned')}></i>
                          ) : topic.is_announcement ? (
                            <i className="bi bi-megaphone-fill topic-icon announcement" title={t('forums.categoryPage.table.announcement')}></i>
                          ) : topic.is_locked ? (
                            <i className="bi bi-lock-fill topic-icon locked" title={t('forums.categoryPage.table.locked')}></i>
                          ) : (
                            <i className="bi bi-folder2 topic-icon normal" title={t('forums.categoryPage.table.normal')}></i>
                          )}
                        </div>
                        <div className="topic-content">
                          <div className="topic-title-row">
                            <Link
                              to={`/forums/category/${categorySlug}/topic/${topic.id}`}
                              className="topic-title-link"
                            >
                              {topic.title}
                            </Link>
                            {/* New indicator for recent topics */}
                            {(() => {
                              const daysSinceCreated = Math.floor((Date.now() - new Date(topic.created_at).getTime()) / (1000 * 60 * 60 * 24));
                              return daysSinceCreated <= 3 && <span className="topic-new-badge">{t('forums.categoryPage.table.new')}</span>;
                            })()}
                            {/* Page numbers for multi-page topics */}
                            {topic.post_count > 20 && (
                              <div className="topic-pages">
                                {Array.from({ length: Math.min(3, Math.ceil(topic.post_count / 20)) }, (_, i) => (
                                  <Link
                                    key={i + 1}
                                    to={`/forums/category/${categorySlug}/topic/${topic.id}?page=${i + 1}`}
                                    className="topic-page-link"
                                  >
                                    {i + 1}
                                  </Link>
                                ))}
                                {Math.ceil(topic.post_count / 20) > 3 && (
                                  <>
                                    <span className="topic-page-ellipsis">...</span>
                                    <Link
                                      to={`/forums/category/${categorySlug}/topic/${topic.id}?page=${Math.ceil(topic.post_count / 20)}`}
                                      className="topic-page-link"
                                    >
                                      {Math.ceil(topic.post_count / 20)}
                                    </Link>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Author */}
                      <div className="topic-cell author-info">
                        <div className="author-name">
                          {topic.created_by_user?.display_name ||
                            topic.created_by_user?.username ||
                            t('forums.topic.anonymous')}
                        </div>
                        <div className="author-date">
                          {new Date(topic.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit'
                          }).replace(/\//g, '-')}
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="topic-cell topic-stats">
                        <div className="stats-container">
                          <div className="stat-number replies-count">
                            {Math.max(0, topic.post_count - 1) || 0}
                          </div>
                          <div className="stat-number views-count">
                            {topic.view_count || 0}
                          </div>
                        </div>
                      </div>

                      {/* Last Post */}
                      <div className="topic-cell last-post-info">
                        <div className="last-post-author">
                          {topic.last_activity_user?.display_name ||
                            topic.last_activity_user?.username ||
                            topic.created_by_user?.display_name ||
                            topic.created_by_user?.username ||
                            t('forums.topic.anonymous')}
                        </div>
                        <div className="last-post-time">
                          {(() => {
                            const lastActivityDate = new Date(topic.last_activity_at || topic.created_at);
                            const now = new Date();
                            const diffHours = Math.floor((now.getTime() - lastActivityDate.getTime()) / (1000 * 60 * 60));
                            const diffDays = Math.floor(diffHours / 24);

                            if (diffHours < 24) {
                              return t('forums.time.hoursAgo', { count: diffHours });
                            } else if (diffDays < 7) {
                              return t('forums.time.daysAgo', { count: diffDays });
                            } else {
                              return lastActivityDate.toLocaleDateString('en-US', {
                                month: '2-digit',
                                day: '2-digit'
                              }).replace('/', '-');
                            }
                          })()}
                        </div>
                      </div>

                      {/* Admin Pin/Unpin Control */}
                      {isAdmin && (
                        <div className="topic-cell pin-control">
                          <button
                            className={`pin-btn ${topic.is_pinned ? 'pinned' : ''}`}
                            onClick={() => handleTogglePin(topic.id, topic.is_pinned)}
                            disabled={pinningTopics.has(topic.id)}
                            title={topic.is_pinned ? 'Unpin topic' : 'Pin topic'}
                          >
                            {pinningTopics.has(topic.id) ? (
                              <PageLoader size="small" />
                            ) : topic.is_pinned ? (
                              <i className="bi bi-pin-angle-fill"></i>
                            ) : (
                              <i className="bi bi-pin-angle"></i>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Login Prompt for Non-authenticated Users */}
            {!user && (
              <div className="forum-login-prompt">
                <div className="login-prompt-content">
                  <h4>{t('forums.categoryPage.loginPrompt.title')}</h4>
                  <p>{t('forums.categoryPage.loginPrompt.subtitle')}</p>
                  <Link to="/auth" className="btn btn-light">
                    <i className="bi bi-box-arrow-in-right"></i>
                    {t('forums.categoryPage.loginPrompt.button')}
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div >

        {/* Floating New Topic Button - Enhanced CTA */}
        {
          user && (
            <Link
              to={`/forums/category/${categorySlug}/create-topic`}
              className="floating-new-topic-btn"
              title="Start a new discussion"
            >
              <i className="bi bi-plus-lg"></i>
            </Link>
          )
        }
      </div >
    </BasePageLayout >
  );
};

export default ForumCategoryPage;